import Reveal from "@/components/Reveal";
import { useSEO } from "@/lib/useSEO";
import { GLOBAL_DISCLAIMER } from "@/data/content";

const PAGES = {
  disclaimer: {
    title: "Disclaimer",
    intro: "Professional Website Disclaimer",
    sections: [
      { heading: "General Information Only", body: "The information available on this website is provided solely for general informational purposes. It is not intended to solicit legal work or advertise professional services. Access to this website is voluntary." },
      { heading: "No Legal Advice", body: "The material on this website does not constitute legal advice and should not be relied upon as a substitute for advice based on the facts and documents of a particular matter. Legal remedies depend on the facts, documents, applicable law and procedural stage of each matter." },
      { heading: "No Advocate-Client Relationship", body: "Viewing this website, sending an enquiry, uploading documents or receiving an acknowledgement does not create an advocate-client relationship. Any professional engagement is subject to conflict checks, document review, availability and formal written confirmation." },
      { heading: "No Guarantee of Outcome", body: "Nothing on this website promises, guarantees or implies any result, remedy or outcome. The website does not display success rates, client testimonials, case-result showcases or comparative claims." },
      { heading: "Third-Party References", body: "References to statutes, courts or official sources are provided for general awareness. Users should verify the current position of law from official publications before acting." },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    intro: "How preliminary information is handled",
    sections: [
      { heading: "Information Collected", body: "When you submit the confidential matter intake form, the website collects the contact details, matter summary and any documents you choose to provide. Basic technical information may be processed for security and analytics with privacy controls." },
      { heading: "Purpose of Use", body: "Information submitted is used solely to conduct a preliminary review, perform conflict checks, and communicate the appropriate next step. It is not sold, rented or shared with third parties for marketing." },
      { heading: "Confidentiality", body: "Information is handled in accordance with applicable professional responsibilities and confidentiality requirements. Visitors are advised not to submit privileged or highly sensitive material through the public form." },
      { heading: "Retention and Security", body: "Enquiry records are retained for administrative purposes and protected by access controls. Data transfer is encrypted. You may request removal of your enquiry information by writing to the office email published on this website." },
      { heading: "Cookies", body: "This website uses minimal storage: a record of your disclaimer acknowledgement and, for administrative users, a secure session cookie. Analytics, where enabled, are configured with privacy controls." },
    ],
  },
  terms: {
    title: "Terms of Use",
    intro: "Conditions governing use of this website",
    sections: [
      { heading: "Acceptance", body: "By accessing this website after acknowledging the entry disclaimer, you accept these terms of use. If you do not accept them, please discontinue use of the website." },
      { heading: "Permitted Use", body: "Content may be read and downloaded for personal, non-commercial informational use. Systematic scraping, reproduction for commercial purposes, or misrepresentation of the content is not permitted." },
      { heading: "Accuracy", body: "Reasonable care is taken in preparing content; however, the law changes and general information may not reflect the position applicable to a specific matter. Each article carries a review date." },
      { heading: "Intellectual Property", body: "The SMART LEGAL SOLUTIONS wordmark, monogram and website content are the property of the office and may not be reproduced without written permission." },
      { heading: "Jurisdiction", body: "Use of this website is subject to the laws of India, and courts at Vijayawada, Andhra Pradesh shall have jurisdiction over disputes arising from website use, to the extent permitted by law." },
    ],
  },
};

export default function Legal({ page }) {
  const content = PAGES[page] || PAGES.disclaimer;
  useSEO(`${content.title} | Smart Legal Solutions`, `${content.title} for the Smart Legal Solutions website.`);

  return (
    <div data-testid={`legal-page-${page}`} className="bg-warm pt-32">
      <div className="mx-auto max-w-3xl px-5 pb-24 lg:px-0">
        <Reveal>
          <p className="mono-label-dark">Smart Legal Solutions</p>
          <h1 className="mt-4 font-serif text-4xl font-medium leading-[1.1] tracking-tight text-navy sm:text-5xl">
            {content.title}
          </h1>
          <div className="gold-hairline mt-8" />
          <p className="mt-8 font-serif text-xl italic text-navy/60">{content.intro}</p>
        </Reveal>
        {content.sections.map((s, i) => (
          <Reveal key={s.heading} className="mt-12">
            <div className="flex items-baseline gap-5">
              <span className="font-mono text-xs tracking-[0.2em] text-gold">{String(i + 1).padStart(2, "0")}</span>
              <h2 className="font-serif text-2xl font-medium text-navy">{s.heading}</h2>
            </div>
            <p className="mt-4 text-base leading-[1.85] text-navy/65 sm:pl-11">{s.body}</p>
          </Reveal>
        ))}
        <Reveal className="mt-14 border border-navy/15 bg-ivory p-8">
          <p className="text-sm leading-relaxed text-navy/60">{GLOBAL_DISCLAIMER}</p>
        </Reveal>
      </div>
    </div>
  );
}
